import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  FitnessCenter as LessonIcon,
} from "@mui/icons-material";
import DataTable from "../DataTable";
import { formatCurrency, profitColor } from "../../utils/formatters";

const lessonProfitColumns = () => [
  {
    key: "name",
    label: "Rodzaj zajęć",
    render: (row) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.lesson_type_name}</Typography>,
  },
  { key: "lesson_count", label: "Zajęcia", align: "right" },
  {
    key: "single_entry_revenue",
    label: "Jed. (PLN)",
    align: "right",
    render: (row) => formatCurrency(row.single_entry_revenue),
  },
  { key: "single_entry_count", label: "Jed. (szt.)", align: "right" },
  {
    key: "pass_revenue",
    label: "Karnety (PLN)",
    align: "right",
    render: (row) => formatCurrency(row.pass_revenue),
  },
  { key: "pass_attendances", label: "Karnety (szt.)", align: "right" },
  {
    key: "trainer_cost",
    label: "Koszt trenera",
    align: "right",
    render: (row) => formatCurrency(row.trainer_cost),
  },
  {
    key: "profit",
    label: "Zysk",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: profitColor(row.profit) }}>
        {formatCurrency(row.profit)}
      </Typography>
    ),
  },
];

const trainerProfitColumns = () => [
  {
    key: "name",
    label: "Trener",
    render: (row) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.trainer_name}</Typography>,
  },
  { key: "lesson_count", label: "Zajęcia", align: "right" },
  {
    key: "single_entry_revenue",
    label: "Jed. (PLN)",
    align: "right",
    render: (row) => formatCurrency(row.single_entry_revenue),
  },
  { key: "single_entry_count", label: "Jed. (szt.)", align: "right" },
  {
    key: "pass_revenue",
    label: "Karnety (PLN)",
    align: "right",
    render: (row) => formatCurrency(row.pass_revenue),
  },
  { key: "pass_attendances", label: "Karnety (szt.)", align: "right" },
  {
    key: "trainer_cost",
    label: "Koszt trenera",
    align: "right",
    render: (row) => formatCurrency(row.trainer_cost),
  },
  {
    key: "profit",
    label: "Zysk",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: profitColor(row.profit) }}>
        {formatCurrency(row.profit)}
      </Typography>
    ),
  },
];

const LocationAccordionRow = ({ loc, isMobile }) => {
  const [mode, setMode] = useState("lessons");
  const locTotalRevenue = (loc.single_entry_revenue || 0) + (loc.pass_revenue || 0);

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, width: "100%" }}>
          <LocationIcon sx={{ color: "text.secondary", mr: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1, minWidth: 120 }}>
            {loc.location_name}
          </Typography>
          <Chip label={`${loc.lesson_count} zajęć`} size="small" />
          <Chip label={`${loc.single_entry_count} jednorazowych`} size="small" variant="outlined" />
          <Chip label={`${loc.pass_attendances} karnetem`} size="small" variant="outlined" />
          <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              Przychód: <strong>{formatCurrency(locTotalRevenue)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Koszty: <strong>{formatCurrency(loc.total_cost)}</strong>
            </Typography>
            <Chip
              label={formatCurrency(loc.profit)}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: loc.profit >= 0 ? "success.light" : "error.light",
                color: loc.profit >= 0 ? "success.dark" : "error.dark",
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Jednorazowe</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.single_entry_revenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {loc.single_entry_count} wejść
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Przychód z karnetów</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.pass_revenue)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Koszt trenera</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.trainer_cost)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Koszt lokalizacji</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.location_cost)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Zysk</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: profitColor(loc.profit) }}>
                {formatCurrency(loc.profit)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Online (PayU)</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.paid_via_payu)}
              </Typography>
              {loc.debt_cleared_amount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  ↳ w tym z długu: {formatCurrency(loc.debt_cleared_amount)}
                  {loc.debt_cleared_count > 0 ? ` (${loc.debt_cleared_count} wejść)` : ""}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Gotówka (user)</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.paid_cash)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">Oczyszczone przez admina</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(loc.admin_cleared_amount)}
              </Typography>
              {loc.admin_cleared_count > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {loc.admin_cleared_count} wejść
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v !== null && setMode(v)}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="lessons">
            <LessonIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Per zajęcia
          </ToggleButton>
          <ToggleButton value="trainers">
            <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Per trener
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "lessons" && (
          <DataTable
            size={isMobile ? "small" : "medium"}
            rows={loc.by_lesson_type}
            getRowKey={(row) => row.lesson_type_id}
            columns={lessonProfitColumns()}
          />
        )}

        {mode === "trainers" && (
          <DataTable
            size={isMobile ? "small" : "medium"}
            rows={loc.by_trainer}
            getRowKey={(row) => row.trainer_id}
            columns={trainerProfitColumns()}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default LocationAccordionRow;
